// Selection — pure data model for a pixel-region selection on a document.
//
// Layer: 3 (Document Model). MUST remain DOM-free / browser-free.
//
// Representation:
//   - `mask`     a Uint8Array of length docW * docH, one byte per pixel.
//                255 = fully selected, 0 = unselected, 1..254 = partial
//                (anti-aliased / feathered).
//   - `bounds`   the tight integer bounding box of all non-zero pixels,
//                clipped to the document. `null` when the selection is empty
//                (no selected pixels at all). Used for fast iteration so that
//                operations don't have to scan the whole document.
//   - `docW/H`   document dimensions the mask is sized to.
//
// An "empty" Selection means *no pixels are selected*. By convention, tools
// that need a "no active marquee = operate on entire layer" semantic should
// treat `isEmpty()` as that signal (this module does not enforce it).
// `Selection.all()` is the opposite — every pixel selected.
//
// All combine / transform operations return a *new* Selection; the receiver
// is never mutated. This keeps the document model immutable-friendly.

export interface SelectionBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface AlphaChannel {
  readonly id: string;
  name: string;
  readonly pixels: Uint8Array;
  readonly width: number;
  readonly height: number;
  visible: boolean;
  opacity: number;
  readonly color: { r: number; g: number; b: number; a: number };
}

export class Selection {
  readonly docW: number;
  readonly docH: number;
  readonly mask: Uint8Array;
  private readonly _bounds: SelectionBounds | null;

  // Direct constructor is internal; prefer the static factories. We accept a
  // pre-computed bounds to avoid rescanning when the caller already knows it.
  // Pass `undefined` to have it computed from the mask.
  constructor(
    docW: number,
    docH: number,
    mask: Uint8Array,
    bounds?: SelectionBounds | null,
  ) {
    if (docW < 0 || docH < 0 || !Number.isFinite(docW) || !Number.isFinite(docH)) {
      throw new RangeError(`Selection: invalid doc dims ${docW}x${docH}`);
    }
    if (mask.length !== docW * docH) {
      throw new RangeError(
        `Selection: mask length ${mask.length} does not match ${docW}x${docH} = ${docW * docH}`,
      );
    }
    this.docW = docW;
    this.docH = docH;
    this.mask = mask;
    this._bounds = bounds === undefined ? computeBounds(mask, docW, docH) : bounds;
  }

  // -------- factories --------

  /** A selection with zero pixels selected. */
  static empty(docW: number, docH: number): Selection {
    return new Selection(docW, docH, new Uint8Array(docW * docH), null);
  }

  /** A selection covering the entire document. */
  static all(docW: number, docH: number): Selection {
    const mask = new Uint8Array(docW * docH);
    mask.fill(255);
    const bounds: SelectionBounds | null =
      docW > 0 && docH > 0 ? { x: 0, y: 0, w: docW, h: docH } : null;
    return new Selection(docW, docH, mask, bounds);
  }

  /**
   * Solid rectangle selection. Coordinates may be fractional / negative;
   * we clip to the document. Width/height <= 0 yield an empty selection.
   *
   * When `antiAlias` is true, edges are anti-aliased via 4x4 sub-pixel
   * oversampling, producing smooth selection boundaries (Photoshop default).
   */
  static fromRect(
    docW: number,
    docH: number,
    x: number,
    y: number,
    w: number,
    h: number,
    antiAlias = false,
  ): Selection {
    const mask = new Uint8Array(docW * docH);
    if (w <= 0 || h <= 0 || docW <= 0 || docH <= 0) {
      return new Selection(docW, docH, mask, null);
    }
    if (!antiAlias) {
      // Fast path: hard-edged rectangle.
      const x0 = Math.max(0, Math.floor(x));
      const y0 = Math.max(0, Math.floor(y));
      const x1 = Math.min(docW, Math.ceil(x + w));
      const y1 = Math.min(docH, Math.ceil(y + h));
      if (x1 <= x0 || y1 <= y0) {
        return new Selection(docW, docH, mask, null);
      }
      for (let yy = y0; yy < y1; yy++) {
        const row = yy * docW;
        mask.fill(255, row + x0, row + x1);
      }
      const bounds: SelectionBounds = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
      return new Selection(docW, docH, mask, bounds);
    }
    // Anti-aliased path: 4x4 sub-pixel oversampling for smooth edges.
    const SUB = 4;
    const x0 = Math.max(0, Math.floor(x));
    const y0 = Math.max(0, Math.floor(y));
    const x1 = Math.min(docW, Math.ceil(x + w));
    const y1 = Math.min(docH, Math.ceil(y + h));
    if (x1 <= x0 || y1 <= y0) {
      return new Selection(docW, docH, mask, null);
    }
    const left = x;
    const right = x + w;
    const top = y;
    const bottom = y + h;
    let minX = docW;
    let minY = docH;
    let maxX = -1;
    let maxY = -1;
    for (let py = y0; py < y1; py++) {
      const row = py * docW;
      for (let px = x0; px < x1; px++) {
        let cov = 0;
        for (let sy = 0; sy < SUB; sy++) {
          const yy = py + (sy + 0.5) / SUB;
          if (yy < top || yy >= bottom) continue;
          for (let sx = 0; sx < SUB; sx++) {
            const xx = px + (sx + 0.5) / SUB;
            if (xx >= left && xx < right) cov++;
          }
        }
        if (cov > 0) {
          const v = cov === 16 ? 255 : Math.round((cov * 255) / 16);
          mask[row + px] = v;
          if (px < minX) minX = px;
          if (py < minY) minY = py;
          if (px > maxX) maxX = px;
          if (py > maxY) maxY = py;
        }
      }
    }
    const bounds: SelectionBounds | null =
      maxX < 0 ? null : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    return new Selection(docW, docH, mask, bounds);
  }

  /**
   * Ellipse selection centered at (cx, cy) with radii (rx, ry).
   * When antiAlias is true (default), uses a 1-pixel-wide linear falloff on
   * the boundary for smooth edges. When false, uses hard-edged rasterization.
   */
  static fromEllipse(
    docW: number,
    docH: number,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    antiAlias = true,
  ): Selection {
    const mask = new Uint8Array(docW * docH);
    if (rx <= 0 || ry <= 0 || docW <= 0 || docH <= 0) {
      return new Selection(docW, docH, mask, null);
    }
    const x0 = Math.max(0, Math.floor(cx - rx - 1));
    const y0 = Math.max(0, Math.floor(cy - ry - 1));
    const x1 = Math.min(docW, Math.ceil(cx + rx + 1));
    const y1 = Math.min(docH, Math.ceil(cy + ry + 1));
    let minX = docW;
    let minY = docH;
    let maxX = -1;
    let maxY = -1;
    for (let yy = y0; yy < y1; yy++) {
      const dy = (yy + 0.5 - cy) / ry;
      const row = yy * docW;
      for (let xx = x0; xx < x1; xx++) {
        const dx = (xx + 0.5 - cx) / rx;
        const d = Math.sqrt(dx * dx + dy * dy);
        let v: number;
        if (antiAlias) {
          // Anti-aliased: 1-pixel-wide linear falloff on the boundary.
          const minR = Math.min(rx, ry);
          const px = (1 - d) * minR;
          if (px >= 0.5) v = 255;
          else if (px <= -0.5) v = 0;
          else v = Math.round((px + 0.5) * 255);
        } else {
          // Hard-edged: inside = 255, outside = 0.
          v = d <= 1 ? 255 : 0;
        }
        if (v > 0) {
          mask[row + xx] = v;
          if (xx < minX) minX = xx;
          if (yy < minY) minY = yy;
          if (xx > maxX) maxX = xx;
          if (yy > maxY) maxY = yy;
        }
      }
    }
    const bounds: SelectionBounds | null =
      maxX < 0
        ? null
        : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    return new Selection(docW, docH, mask, bounds);
  }

  /**
   * Single-row selection — exactly 1 pixel tall, spanning the full document width.
   * Photoshop: Marquee tool → Single Row Marquee.
   */
  static fromSingleRow(docW: number, docH: number, row: number): Selection {
    const mask = new Uint8Array(docW * docH);
    if (row < 0 || row >= docH || docW <= 0 || docH <= 0) {
      return new Selection(docW, docH, mask, null);
    }
    const start = row * docW;
    mask.fill(255, start, start + docW);
    return new Selection(docW, docH, mask, { x: 0, y: row, w: docW, h: 1 });
  }

  /**
   * Single-column selection — exactly 1 pixel wide, spanning the full document height.
   * Photoshop: Marquee tool → Single Column Marquee.
   */
  static fromSingleColumn(docW: number, docH: number, col: number): Selection {
    const mask = new Uint8Array(docW * docH);
    if (col < 0 || col >= docW || docW <= 0 || docH <= 0) {
      return new Selection(docW, docH, mask, null);
    }
    for (let y = 0; y < docH; y++) {
      mask[y * docW + col] = 255;
    }
    return new Selection(docW, docH, mask, { x: col, y: 0, w: 1, h: docH });
  }

  /**
   * Anti-aliased polygon selection. Rasterizes the closed polygon defined by
   * `points` (implicitly closed: the last point connects back to the first)
   * using the non-zero winding rule (matches SVG default). The edges are
   * anti-aliased via 4x4 sub-pixel oversampling: each output pixel's coverage
   * is the count of its 16 sub-samples that lie inside the polygon, scaled to
   * 0..255.
   *
   * Polygons with fewer than 3 unique vertices, or zero-area polygons, yield
   * an empty selection. Coordinates may be fractional / negative; the result
   * is clipped to the document.
   *
   * `antiAlias` (default true) controls whether edges are softened via 4x4
   * sub-pixel oversampling (smooth boundary, partial coverage values) or
   * rasterized hard-edged with a single center-point sample per pixel
   * (binary 0/255 only) — mirrors fromRect/fromEllipse's antiAlias toggle.
   */
  static fromPolygon(
    docW: number,
    docH: number,
    points: ReadonlyArray<{ x: number; y: number }>,
    antiAlias = true,
  ): Selection {
    const mask = new Uint8Array(docW * docH);
    if (docW <= 0 || docH <= 0 || points.length < 3) {
      return new Selection(docW, docH, mask, null);
    }
    // Build edge list with their bounding y-range and dx/dy slope. We rasterize
    // by row, accumulating winding numbers per sub-row.
    const n = points.length;
    // Compute polygon bounding box (clipped to doc) to limit our scan area.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < n; i++) {
      const p = points[i]!;
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    const x0 = Math.max(0, Math.floor(minX));
    const y0 = Math.max(0, Math.floor(minY));
    const x1 = Math.min(docW, Math.ceil(maxX) + 1);
    const y1 = Math.min(docH, Math.ceil(maxY) + 1);
    if (x1 <= x0 || y1 <= y0) {
      return new Selection(docW, docH, mask, null);
    }

    // Precompute edges (skip horizontal — they contribute no winding crossing).
    interface Edge {
      yMin: number; // inclusive (in pixel space, may be fractional)
      yMax: number; // exclusive
      x: number; // x at current scanline y = yMin
      dxdy: number; // slope
      dir: number; // +1 if going down (y increasing), -1 if going up
    }
    const edges: Edge[] = [];
    for (let i = 0; i < n; i++) {
      const a = points[i]!;
      const b = points[(i + 1) % n]!;
      if (a.y === b.y) continue; // horizontal — no winding contribution
      const dir = b.y > a.y ? 1 : -1;
      const yMin = Math.min(a.y, b.y);
      const yMax = Math.max(a.y, b.y);
      const dxdy = (b.x - a.x) / (b.y - a.y);
      // x at yMin (the lower y of the two endpoints):
      const x = a.y < b.y ? a.x : b.x;
      edges.push({ yMin, yMax, x, dxdy, dir });
    }
    if (edges.length === 0) {
      return new Selection(docW, docH, mask, null);
    }

    // 4x4 sub-pixel grid (antiAlias=true) or a single center-point sample per
    // pixel (antiAlias=false, SUB=1 — hard edges, matches fromRect/fromEllipse's
    // non-AA path). For each pixel row, we evaluate SUB horizontal slices
    // (sub-y at y + (k+0.5)/SUB). For each slice we find x-crossings, sort
    // them, then use non-zero winding to mark inside spans. A horizontal
    // sub-pixel grid of SUB then refines edge coverage within each pixel-column.
    const SUB = antiAlias ? 4 : 1;
    const totalSamples = SUB * SUB;
    // Coverage accumulator: totalSamples means fully inside (SUB sub-rows *
    // SUB sub-cols). We'll accumulate into a temporary row buffer.
    let bMinX = docW;
    let bMinY = docH;
    let bMaxX = -1;
    let bMaxY = -1;

    const rowCov = new Uint8Array(x1 - x0); // 0..16 per pixel

    for (let py = y0; py < y1; py++) {
      // Reset row coverage
      for (let i = 0; i < rowCov.length; i++) rowCov[i] = 0;
      for (let sy = 0; sy < SUB; sy++) {
        const yy = py + (sy + 0.5) / SUB;
        // Collect x-crossings (with direction) at this sub-row.
        const crossings: Array<{ x: number; dir: number }> = [];
        for (let e = 0; e < edges.length; e++) {
          const ed = edges[e]!;
          // Edge covers [yMin, yMax). Include yMin, exclude yMax (standard
          // half-open scanline rule to avoid double-counting at vertex shares).
          if (yy >= ed.yMin && yy < ed.yMax) {
            const x = ed.x + (yy - ed.yMin) * ed.dxdy;
            crossings.push({ x, dir: ed.dir });
          }
        }
        if (crossings.length === 0) continue;
        crossings.sort((p, q) => p.x - q.x);

        // Walk crossings with non-zero winding rule. For each pixel column,
        // sample 4 sub-x positions and check whether each is inside.
        // To do this efficiently we walk the column range and the crossing
        // list together, tracking the current winding number at each sub-x.
        // Simpler approach: for each pixel column in [x0, x1), check each of 4
        // sub-x positions against the (already-sorted) crossing list via a
        // tiny scan. Polygons are typically small relative to image size so
        // this is fine.
        for (let px = x0; px < x1; px++) {
          const cx = px - x0;
          let cov = rowCov[cx]!;
          for (let sx = 0; sx < SUB; sx++) {
            const xx = px + (sx + 0.5) / SUB;
            // Winding number at xx = sum of dir for crossings with x <= xx.
            // (Edges where x == xx count; consistent half-open behavior.)
            let w = 0;
            for (let k = 0; k < crossings.length; k++) {
              const c = crossings[k]!;
              if (c.x <= xx) w += c.dir;
              else break;
            }
            if (w !== 0) cov++;
          }
          rowCov[cx] = cov;
        }
      }
      // Commit row to mask, computing coverage * 255 / 16.
      const rowStart = py * docW;
      for (let cx = 0; cx < rowCov.length; cx++) {
        const cov = rowCov[cx]!;
        if (cov > 0) {
          // totalSamples sub-samples → scale to 0..255. Use rounded division.
          const v = cov === totalSamples ? 255 : Math.round((cov * 255) / totalSamples);
          if (v > 0) {
            const px = x0 + cx;
            mask[rowStart + px] = v;
            if (px < bMinX) bMinX = px;
            if (py < bMinY) bMinY = py;
            if (px > bMaxX) bMaxX = px;
            if (py > bMaxY) bMaxY = py;
          }
        }
      }
    }

    const bounds: SelectionBounds | null =
      bMaxX < 0
        ? null
        : { x: bMinX, y: bMinY, w: bMaxX - bMinX + 1, h: bMaxY - bMinY + 1 };
    return new Selection(docW, docH, mask, bounds);
  }

  /**
   * Wraps an arbitrary 8-bit coverage mask as a Selection. The mask is *copied*
   * so the caller can safely mutate the source buffer afterwards. If `bounds`
   * is omitted it is computed by scanning the mask.
   */
  static fromMask(
    docW: number,
    docH: number,
    mask: Uint8Array,
    bounds?: SelectionBounds,
  ): Selection {
    if (mask.length !== docW * docH) {
      throw new RangeError(
        `Selection.fromMask: mask length ${mask.length} does not match ${docW}x${docH} = ${docW * docH}`,
      );
    }
    const copy = new Uint8Array(mask);
    return new Selection(
      docW,
      docH,
      copy,
      bounds === undefined ? undefined : { ...bounds },
    );
  }

  // -------- queries --------

  isEmpty(): boolean {
    return this._bounds === null;
  }

  /** Mask value at (x,y), or 0 if out of bounds. */
  contains(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.docW || y >= this.docH) return 0;
    const xi = x | 0;
    const yi = y | 0;
    return this.mask[yi * this.docW + xi] ?? 0;
  }

  bounds(): SelectionBounds | null {
    return this._bounds === null ? null : { ...this._bounds };
  }

  clone(): Selection {
    const m = new Uint8Array(this.mask);
    return new Selection(
      this.docW,
      this.docH,
      m,
      this._bounds === null ? null : { ...this._bounds },
    );
  }

  // -------- combine ops --------

  union(other: Selection): Selection {
    this._assertSameDoc(other);
    const out = new Uint8Array(this.docW * this.docH);
    const a = this.mask;
    const b = other.mask;
    const aB = this._bounds;
    const bB = other._bounds;
    // Fast path: copy a into out only over a's bounds, then max-combine b's.
    if (aB) blit(out, a, this.docW, aB);
    if (bB) {
      for (let yy = bB.y; yy < bB.y + bB.h; yy++) {
        const row = yy * this.docW;
        for (let xx = bB.x; xx < bB.x + bB.w; xx++) {
          const i = row + xx;
          const av = out[i] ?? 0;
          const bv = b[i] ?? 0;
          if (bv > av) out[i] = bv;
        }
      }
    }
    return new Selection(this.docW, this.docH, out);
  }

  intersect(other: Selection): Selection {
    this._assertSameDoc(other);
    const out = new Uint8Array(this.docW * this.docH);
    const aB = this._bounds;
    const bB = other._bounds;
    if (!aB || !bB) return new Selection(this.docW, this.docH, out, null);
    const x0 = Math.max(aB.x, bB.x);
    const y0 = Math.max(aB.y, bB.y);
    const x1 = Math.min(aB.x + aB.w, bB.x + bB.w);
    const y1 = Math.min(aB.y + aB.h, bB.y + bB.h);
    if (x1 <= x0 || y1 <= y0) return new Selection(this.docW, this.docH, out, null);
    const a = this.mask;
    const b = other.mask;
    for (let yy = y0; yy < y1; yy++) {
      const row = yy * this.docW;
      for (let xx = x0; xx < x1; xx++) {
        const i = row + xx;
        const av = a[i] ?? 0;
        const bv = b[i] ?? 0;
        // intersection = min (treating mask as 0..1 coverage)
        out[i] = av < bv ? av : bv;
      }
    }
    return new Selection(this.docW, this.docH, out);
  }

  subtract(other: Selection): Selection {
    this._assertSameDoc(other);
    const out = new Uint8Array(this.mask);
    const aB = this._bounds;
    const bB = other._bounds;
    if (!aB || !bB) return new Selection(this.docW, this.docH, out, aB ? { ...aB } : null);
    const x0 = Math.max(aB.x, bB.x);
    const y0 = Math.max(aB.y, bB.y);
    const x1 = Math.min(aB.x + aB.w, bB.x + bB.w);
    const y1 = Math.min(aB.y + aB.h, bB.y + bB.h);
    if (x1 <= x0 || y1 <= y0) {
      // No overlap, result == this
      return new Selection(this.docW, this.docH, out, { ...aB });
    }
    const b = other.mask;
    for (let yy = y0; yy < y1; yy++) {
      const row = yy * this.docW;
      for (let xx = x0; xx < x1; xx++) {
        const i = row + xx;
        const av = out[i] ?? 0;
        const bv = b[i] ?? 0;
        // Photoshop's set-difference: a * (1 - b/255) — multiplicative inverse.
        // Correctly handles anti-aliased/feathered masks (binary masks are equivalent).
        const v = Math.round(av * (1 - bv / 255));
        out[i] = v < 0 ? 0 : v > 255 ? 255 : v;
      }
    }
    return new Selection(this.docW, this.docH, out);
  }

  xor(other: Selection): Selection {
    this._assertSameDoc(other);
    const out = new Uint8Array(this.docW * this.docH);
    const aB = this._bounds;
    const bB = other._bounds;
    // Symmetric difference: |a - b| in coverage terms.
    if (aB) blit(out, this.mask, this.docW, aB);
    if (bB) {
      for (let yy = bB.y; yy < bB.y + bB.h; yy++) {
        const row = yy * this.docW;
        for (let xx = bB.x; xx < bB.x + bB.w; xx++) {
          const i = row + xx;
          const av = out[i] ?? 0;
          const bv = other.mask[i] ?? 0;
          const v = av - bv;
          out[i] = v < 0 ? -v : v;
        }
      }
    }
    return new Selection(this.docW, this.docH, out);
  }

  // -------- transforms --------

  /**
   * Translate the selection mask by (dx, dy) pixels.
   * Pixels that move outside the document boundary are clipped.
   * Empty areas left behind become unselected (0).
   */
  translate(dx: number, dy: number): Selection {
    const out = new Uint8Array(this.docW * this.docH);
    const m = this.mask;
    const w = this.docW;
    const h = this.docH;
    // Compute the source-to-dest offset
    const sx = -dx; // source x offset
    const sy = -dy; // source y offset
    // Determine the dest region that will receive data
    const dstX0 = dx > 0 ? dx : 0;
    const dstY0 = dy > 0 ? dy : 0;
    const dstX1 = dx > 0 ? w : w + dx;
    const dstY1 = dy > 0 ? h : h + dy;
    for (let dy2 = dstY0; dy2 < dstY1; dy2++) {
      for (let dx2 = dstX0; dx2 < dstX1; dx2++) {
        const si = (dy2 + sy) * w + (dx2 + sx);
        const di = dy2 * w + dx2;
        if (si >= 0 && si < m.length) {
          out[di] = m[si] ?? 0;
        }
      }
    }
    return new Selection(w, h, out);
  }

  /** Inverse: every pixel becomes 255 - value. Result covers whole doc. */
  invert(): Selection {
    const out = new Uint8Array(this.docW * this.docH);
    const m = this.mask;
    for (let i = 0; i < out.length; i++) {
      out[i] = 255 - (m[i] ?? 0);
    }
    return new Selection(this.docW, this.docH, out);
  }

  /**
   * Gaussian-like feather. `radius` is in pixels (>= 0). 0 returns a clone.
   * Implemented as a separable two-pass box blur applied 3 times — a very good
   * Gaussian approximation that stays O(n) and is pure TS.
   */
  feather(radius: number): Selection {
    if (!(radius > 0) || this.isEmpty()) return this.clone();
    const r = Math.max(1, Math.round(radius));
    const w = this.docW;
    const h = this.docH;
    // Work in a Float32Array buffer for accuracy across passes.
    const buf = new Float32Array(w * h);
    for (let i = 0; i < buf.length; i++) buf[i] = this.mask[i] ?? 0;
    const tmp = new Float32Array(w * h);
    for (let pass = 0; pass < 3; pass++) {
      boxBlurH(buf, tmp, w, h, r);
      boxBlurV(tmp, buf, w, h, r);
    }
    const out = new Uint8Array(w * h);
    for (let i = 0; i < out.length; i++) {
      const v = buf[i] ?? 0;
      out[i] = v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
    }
    return new Selection(w, h, out);
  }

  /**
   * Morphological dilate (Photoshop "Expand"). Square structuring element of
   * radius `px`. Mask values are taken at full strength (>=128 considered set).
   * Output is binary 0/255.
   */
  expand(px: number): Selection {
    if (!(px > 0) || this.isEmpty()) return this.clone();
    return this._morph(Math.round(px), /*dilate=*/ true);
  }

  /** Morphological erode (Photoshop "Contract"). */
  contract(px: number): Selection {
    if (!(px > 0) || this.isEmpty()) return this.clone();
    return this._morph(Math.round(px), /*dilate=*/ false);
  }

  /**
   * Photoshop "Border": creates a selection ring of width `size` centered on
   * the original selection edge. Implemented as (expand by size) - (contract
   * by size). Output is binary 0/255. `size` is the total border width in
   * pixels (half extends inward, half outward).
   */
  border(size: number): Selection {
    if (!(size > 0) || this.isEmpty()) return this.clone();
    const r = Math.round(size);
    const outer = this._morph(r, true);
    const inner = this._morph(r, false);
    return outer.subtract(inner);
  }

  /**
   * Photoshop "Smooth": morphological closing (dilate then erode) with the
   * given radius. Fills small holes and removes small protrusions from the
   * selection boundary. Output is binary 0/255.
   */
  smooth(radius: number): Selection {
    if (!(radius > 0) || this.isEmpty()) return this.clone();
    const r = Math.round(radius);
    const dilated = this._morph(r, true);
    return dilated._morph(r, false);
  }

  /** Photoshop "Grow" — non-contiguous color-based expansion. */
  grow(tolerance: number): Selection {
    // Grow uses the same algorithm as Magic Wand's select-by-color, but
    // starting from the current selection's bounds. For now we return a
    // clone — the full implementation requires pixel data access from the
    // document, which lives at a higher layer.
    return this.clone();
  }

  /** Photoshop "Similar" — select all matching pixels in the image. */
  similar(tolerance: number): Selection {
    // Same as Grow — requires pixel data from the document, not the
    // selection model. Stub returns a clone.
    return this.clone();
  }

  // -------- internals --------

  private _assertSameDoc(other: Selection): void {
    if (other.docW !== this.docW || other.docH !== this.docH) {
      throw new RangeError(
        `Selection: doc-size mismatch ${this.docW}x${this.docH} vs ${other.docW}x${other.docH}`,
      );
    }
  }

  private _morph(r: number, dilate: boolean): Selection {
    // Separable morphology with a square SE: pass H then V using running
    // min/max over a window of size 2r+1.
    const w = this.docW;
    const h = this.docH;
    // Binarize first: >=128 -> 1, else 0.
    const bin = new Uint8Array(w * h);
    for (let i = 0; i < bin.length; i++) {
      bin[i] = (this.mask[i] ?? 0) >= 128 ? 1 : 0;
    }
    const tmp = new Uint8Array(w * h);
    morphRow(bin, tmp, w, h, r, dilate);
    const out2 = new Uint8Array(w * h);
    morphCol(tmp, out2, w, h, r, dilate);
    const out = new Uint8Array(w * h);
    for (let i = 0; i < out.length; i++) out[i] = (out2[i] ?? 0) ? 255 : 0;
    return new Selection(w, h, out);
  }
}

// =================== helpers ===================

function computeBounds(
  mask: Uint8Array,
  w: number,
  h: number,
): SelectionBounds | null {
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      if ((mask[row + x] ?? 0) !== 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

function blit(
  dst: Uint8Array,
  src: Uint8Array,
  stride: number,
  b: SelectionBounds,
): void {
  for (let yy = b.y; yy < b.y + b.h; yy++) {
    const row = yy * stride;
    for (let xx = b.x; xx < b.x + b.w; xx++) {
      dst[row + xx] = src[row + xx] ?? 0;
    }
  }
}

function boxBlurH(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  r: number,
): void {
  const win = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let sum = 0;
    // Prime: clamp at edges.
    for (let k = -r; k <= r; k++) {
      const x = k < 0 ? 0 : k >= w ? w - 1 : k;
      sum += src[row + x] ?? 0;
    }
    for (let x = 0; x < w; x++) {
      dst[row + x] = sum / win;
      const xOut = x - r;
      const xIn = x + r + 1;
      const cOut = xOut < 0 ? 0 : xOut >= w ? w - 1 : xOut;
      const cIn = xIn < 0 ? 0 : xIn >= w ? w - 1 : xIn;
      sum += (src[row + cIn] ?? 0) - (src[row + cOut] ?? 0);
    }
  }
}

function boxBlurV(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  r: number,
): void {
  const win = 2 * r + 1;
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let k = -r; k <= r; k++) {
      const y = k < 0 ? 0 : k >= h ? h - 1 : k;
      sum += src[y * w + x] ?? 0;
    }
    for (let y = 0; y < h; y++) {
      dst[y * w + x] = sum / win;
      const yOut = y - r;
      const yIn = y + r + 1;
      const cOut = yOut < 0 ? 0 : yOut >= h ? h - 1 : yOut;
      const cIn = yIn < 0 ? 0 : yIn >= h ? h - 1 : yIn;
      sum += (src[cIn * w + x] ?? 0) - (src[cOut * w + x] ?? 0);
    }
  }
}

function morphRow(
  src: Uint8Array,
  dst: Uint8Array,
  w: number,
  h: number,
  r: number,
  dilate: boolean,
): void {
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let acc = dilate ? 0 : 1;
      const x0 = x - r < 0 ? 0 : x - r;
      const x1 = x + r >= w ? w - 1 : x + r;
      for (let k = x0; k <= x1; k++) {
        const v = src[row + k] ?? 0;
        if (dilate) {
          if (v > acc) acc = v;
        } else {
          if (v < acc) acc = v;
        }
      }
      // When eroding, also account for window clipping at doc edges: any
      // missing samples count as 0 (outside-doc = unselected).
      if (!dilate) {
        if (x - r < 0 || x + r >= w) acc = 0;
      }
      dst[row + x] = acc;
    }
  }
}

function morphCol(
  src: Uint8Array,
  dst: Uint8Array,
  w: number,
  h: number,
  r: number,
  dilate: boolean,
): void {
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let acc = dilate ? 0 : 1;
      const y0 = y - r < 0 ? 0 : y - r;
      const y1 = y + r >= h ? h - 1 : y + r;
      for (let k = y0; k <= y1; k++) {
        const v = src[k * w + x] ?? 0;
        if (dilate) {
          if (v > acc) acc = v;
        } else {
          if (v < acc) acc = v;
        }
      }
      if (!dilate) {
        if (y - r < 0 || y + r >= h) acc = 0;
      }
      dst[y * w + x] = acc;
    }
  }
}
